import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { isAxiosError } from 'axios';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField, FormGrid, FormSection } from '@/components/forms/FormSection';
import {
  CAMPAIGN_CHANNELS,
  CAMPAIGN_OBJECTIVES,
  CURRENCIES,
} from '@/lib/validation';

/**
 * CreateCampaignModal — extracted from CampaignsPage so the create
 * flow can grow without bloating the list page.
 *
 * Captures the data RevOps actually needs to plan and report on a
 * campaign: objective, channels, budget+currency, target audience,
 * owner, and the expected lead/opp/revenue targets that feed campaign
 * ROI dashboards. Everything beyond name/description is sent
 * forward-compatibly — the backend ignores unknown fields until V164
 * lands so the UI doesn't have to wait for the migration.
 */

interface UserOption {
  id: string;
  displayName: string;
  email: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function CreateCampaignModal({ open, onClose, onCreated }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Identity
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [objective, setObjective] = useState('');

  // Reach
  const [channels, setChannels] = useState<string[]>([]);
  const [targetAudience, setTargetAudience] = useState('');

  // Budget
  const [budgetAmount, setBudgetAmount] = useState('');
  const [currency, setCurrency] = useState('USD');

  // Schedule
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');

  // Ownership
  const [ownerUserId, setOwnerUserId] = useState('');

  // Forecast targets (feed ROI dashboards)
  const [expectedLeadCount, setExpectedLeadCount] = useState('');
  const [expectedOpportunityCount, setExpectedOpportunityCount] = useState('');
  const [expectedRevenueAmount, setExpectedRevenueAmount] = useState('');

  // Tags
  const [tags, setTags] = useState('');

  // Capture form bridge — same UX as before: jump to the form editor
  // pre-bound to this campaign so the user gets a shareable URL.
  const [createForm, setCreateForm] = useState(true);

  const [users, setUsers] = useState<UserOption[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    apiClient
      .get<UserOption[]>('/users?activeOnly=true')
      .then(({ data }) => setUsers(data ?? []))
      .catch(() => setUsers([]));
  }, [open]);

  const dateRangeError = useMemo(() => {
    if (!startsAt || !endsAt) return null;
    return startsAt > endsAt ? t('createCampaign.error.dateRange') : null;
  }, [startsAt, endsAt, t]);

  if (!open) return null;

  function toggleChannel(ch: string) {
    setChannels((cur) =>
      cur.includes(ch) ? cur.filter((c) => c !== ch) : [...cur, ch],
    );
  }

  function reset() {
    setName('');
    setDescription('');
    setObjective('');
    setChannels([]);
    setTargetAudience('');
    setBudgetAmount('');
    setCurrency('USD');
    setStartsAt('');
    setEndsAt('');
    setOwnerUserId('');
    setExpectedLeadCount('');
    setExpectedOpportunityCount('');
    setExpectedRevenueAmount('');
    setTags('');
    setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!name.trim()) return;
    if (dateRangeError) {
      setError(dateRangeError);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body: Record<string, unknown> = {
        name: name.trim(),
        description: description.trim() || undefined,
      };
      if (objective) body.objective = objective;
      if (channels.length > 0) body.channels = channels;
      if (targetAudience.trim()) body.targetAudience = targetAudience.trim();

      if (budgetAmount && !Number.isNaN(Number(budgetAmount))) {
        // Cents — backend uses BIGINT cents to avoid float drift.
        body.budgetCents = Math.round(Number(budgetAmount) * 100);
        body.currency = currency;
      }
      if (startsAt) body.startsAt = startsAt;
      if (endsAt) body.endsAt = endsAt;
      if (ownerUserId) body.ownerUserId = ownerUserId;

      if (expectedLeadCount && !Number.isNaN(Number(expectedLeadCount))) {
        body.expectedLeadCount = Math.round(Number(expectedLeadCount));
      }
      if (expectedOpportunityCount && !Number.isNaN(Number(expectedOpportunityCount))) {
        body.expectedOpportunityCount = Math.round(Number(expectedOpportunityCount));
      }
      if (expectedRevenueAmount && !Number.isNaN(Number(expectedRevenueAmount))) {
        body.expectedRevenueCents = Math.round(Number(expectedRevenueAmount) * 100);
      }

      const tagArray = tags
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      if (tagArray.length > 0) body.tags = tagArray;

      const { data } = await apiClient.post<{ id: string }>('/campaigns', body);
      reset();
      onCreated();
      onClose();
      // Story 53.14 — guide the user straight into the lead-capture form
      // editor with the new campaign pre-selected, so the campaign isn't
      // a dead end. Skipping the form is still possible from the form
      // page; we just default to creating one so the campaign actually
      // becomes shareable.
      if (createForm) {
        navigate(`/admin/lead-capture-forms?newForCampaign=${data.id}`);
      }
    } catch (err) {
      const detail = isAxiosError(err)
        ? (err.response?.data as { detail?: string } | undefined)?.detail
        : null;
      setError(detail ?? t('createCampaign.error.generic'));
    } finally {
      setSubmitting(false);
    }
  }

  function handleCancel() {
    reset();
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('createCampaign.title')}</h3>

        <form onSubmit={handleSubmit} className="space-y-3">
          {/* ───── Identity ───── */}
          <FormSection title={t('createCampaign.section.identity')} defaultOpen>
            <FormField label={t('createCampaign.field.name')} required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                autoFocus
                placeholder={t('createCampaign.placeholder.name')}
              />
            </FormField>
            <FormField label={t('createCampaign.field.description')}>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                rows={3}
              />
            </FormField>
            <FormField label={t('createCampaign.field.objective')}>
              <select
                value={objective}
                onChange={(e) => setObjective(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                <option value="">{t('common.none')}</option>
                {CAMPAIGN_OBJECTIVES.map((o) => (
                  <option key={o} value={o}>
                    {t(`reference.campaignObjective.${o}`)}
                  </option>
                ))}
              </select>
            </FormField>
          </FormSection>

          {/* ───── Reach ───── */}
          <FormSection title={t('createCampaign.section.reach')} defaultOpen>
            <FormField
              label={t('createCampaign.field.channels')}
              hint={t('createCampaign.hint.channels')}
            >
              <div className="flex flex-wrap gap-2">
                {CAMPAIGN_CHANNELS.map((ch) => {
                  const active = channels.includes(ch);
                  return (
                    <button
                      key={ch}
                      type="button"
                      onClick={() => toggleChannel(ch)}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                        active
                          ? 'border-primary bg-primary/15 text-primary'
                          : 'border-border bg-background hover:bg-accent/30'
                      }`}
                      aria-pressed={active}
                    >
                      {t(`reference.campaignChannel.${ch}`)}
                    </button>
                  );
                })}
              </div>
            </FormField>
            <FormField
              label={t('createCampaign.field.targetAudience')}
              hint={t('createCampaign.hint.targetAudience')}
            >
              <textarea
                value={targetAudience}
                onChange={(e) => setTargetAudience(e.target.value)}
                rows={2}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                placeholder={t('createCampaign.placeholder.targetAudience')}
              />
            </FormField>
          </FormSection>

          {/* ───── Budget & schedule ───── */}
          <FormSection title={t('createCampaign.section.budget')} defaultOpen>
            <FormGrid>
              <FormField label={t('createCampaign.field.budget')}>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={budgetAmount}
                  onChange={(e) => setBudgetAmount(e.target.value)}
                />
              </FormField>
              <FormField label={t('createCampaign.field.currency')}>
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {CURRENCIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('createCampaign.field.startsAt')}>
                <Input
                  type="date"
                  value={startsAt}
                  onChange={(e) => setStartsAt(e.target.value)}
                />
              </FormField>
              <FormField
                label={t('createCampaign.field.endsAt')}
                error={dateRangeError}
              >
                <Input
                  type="date"
                  value={endsAt}
                  onChange={(e) => setEndsAt(e.target.value)}
                />
              </FormField>
            </FormGrid>
          </FormSection>

          {/* ───── Ownership ───── */}
          <FormSection title={t('createCampaign.section.ownership')} defaultOpen={false}>
            <FormField label={t('createCampaign.field.owner')}>
              <select
                value={ownerUserId}
                onChange={(e) => setOwnerUserId(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                <option value="">{t('common.none')}</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.displayName} ({u.email})
                  </option>
                ))}
              </select>
            </FormField>
          </FormSection>

          {/* ───── Forecast targets ───── */}
          <FormSection
            title={t('createCampaign.section.forecast')}
            subtitle={t('createCampaign.section.forecastHint')}
            defaultOpen={false}
          >
            <FormGrid cols={3}>
              <FormField label={t('createCampaign.field.expectedLeads')}>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={expectedLeadCount}
                  onChange={(e) => setExpectedLeadCount(e.target.value)}
                />
              </FormField>
              <FormField label={t('createCampaign.field.expectedOpportunities')}>
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={expectedOpportunityCount}
                  onChange={(e) => setExpectedOpportunityCount(e.target.value)}
                />
              </FormField>
              <FormField label={t('createCampaign.field.expectedRevenue')}>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  value={expectedRevenueAmount}
                  onChange={(e) => setExpectedRevenueAmount(e.target.value)}
                />
              </FormField>
            </FormGrid>
            <FormField label={t('createCampaign.field.tags')} hint={t('createCampaign.hint.tags')}>
              <Input
                value={tags}
                onChange={(e) => setTags(e.target.value)}
                placeholder="webinar, q2-2026"
              />
            </FormField>
          </FormSection>

          {/* Capture-form bridge */}
          <label className="flex items-start gap-2 rounded-md border bg-muted/30 p-3 text-sm">
            <input
              type="checkbox"
              checked={createForm}
              onChange={(e) => setCreateForm(e.target.checked)}
              className="mt-0.5 h-4 w-4"
            />
            <div>
              <div className="font-medium">{t('createCampaign.captureForm.title')}</div>
              <div className="text-xs text-muted-foreground">
                {t('createCampaign.captureForm.body')}
              </div>
            </div>
          </label>

          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={handleCancel}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={submitting || !name.trim() || !!dateRangeError}>
              {submitting
                ? t('createCampaign.submit.creating')
                : t('createCampaign.submit.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
